import { toast } from "react-toastify";
import {
  OBJECT_TYPES,
  GRID_CELL_SIZE_M,
  defaultObjectSizes,
  WALL_THICKNESS_M_RENDER,
  CORRIDOR_WALL_THICKNESS_M,
  DEFAULT_MODULE_CELLS_WIDE,
  DEFAULT_MODULE_CELLS_LONG,
  PLACEMENT_RULES,
} from "./configuratorConstants";

import {
  dotProduct,
  subtractVectors,
  normalizeVector,
  perpendicularVector,
  addVectors,
  scaleVector,
  distance,
  rotatePoint,
  projectPolygonOntoAxis,
  checkPolygonsIntersectionSAT,
  findNearestPointOnLineSegment,
  lineSegmentsIntersect,
  getLineIntersectionPoint,
} from "./utils/geometry";

export {
  dotProduct,
  subtractVectors,
  normalizeVector,
  perpendicularVector,
  addVectors,
  scaleVector,
  distance,
  rotatePoint,
  projectPolygonOntoAxis,
  checkPolygonsIntersectionSAT,
  findNearestPointOnLineSegment,
  lineSegmentsIntersect,
  getLineIntersectionPoint,
};

const COLLISION_EPSILON = 1e-1;
const CORNER_TOUCH_TOLERANCE = 1e-4; // Допуск для визначення дотику кутами

export const generateId = (prefix = "id_") =>
  `${prefix}${Date.now().toString(36)}${Math.random().toString(36).substring(2, 7)}`;

export const createInitialWallSegments = (moduleConfig) => {
  const segments = {};
  const cellsWide =
    parseInt(moduleConfig.cellsWide) || DEFAULT_MODULE_CELLS_WIDE;
  const cellsLong =
    parseInt(moduleConfig.cellsLong) || DEFAULT_MODULE_CELLS_LONG;
  const internalWallSegmentsConfig = moduleConfig.internalWallSegments || {};
  const openingSide = moduleConfig.openingSide;

  const isInternalSegment = (key) => {
    const [coords, orientation] = key.split("_");
    const [cxStr, cyStr] = coords.split(",");
    const cellX = parseInt(cxStr, 10);
    const cellY = parseInt(cyStr, 10);
    if (orientation === "h") return cellY > 0 && cellY < cellsLong;
    if (orientation === "v") return cellX > 0 && cellX < cellsWide;
    return false;
  };

  const createSegment = (key, props = {}) => ({
    id: generateId("wall_"),
    type: OBJECT_TYPES.WALL_SEGMENT,
    elements: [],
    thickness: props.thickness
      ? props.thickness
      : isInternalSegment(key)
        ? CORRIDOR_WALL_THICKNESS_M
        : WALL_THICKNESS_M_RENDER,
    ...props,
  });

  if (openingSide !== "top") {
    for (let i = 0; i < cellsWide; i++) {
      segments[`${i},0_h`] = createSegment(`${i},0_h`);
    }
  }
  if (openingSide !== "bottom") {
    for (let i = 0; i < cellsWide; i++) {
      segments[`${i},${cellsLong}_h`] = createSegment(`${i},${cellsLong}_h`);
    }
  }
  if (openingSide !== "left") {
    for (let j = 0; j < cellsLong; j++) {
      segments[`0,${j}_v`] = createSegment(`0,${j}_v`);
    }
  }
  if (openingSide !== "right") {
    for (let j = 0; j < cellsLong; j++) {
      segments[`${cellsWide},${j}_v`] = createSegment(`${cellsWide},${j}_v`);
    }
  }

  for (const [key, data] of Object.entries(internalWallSegmentsConfig)) {
    segments[key] = createSegment(key, data);
  }
  return segments;
};

export function getModuleVertices(module) {
  const w = module.width;
  const h = module.height;
  const x = module.x;
  const y = module.y;
  const angleRad = ((module.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  const localCorners = [
    { x: 0, y: 0 },
    { x: w, y: 0 },
    { x: w, y: h },
    { x: 0, y: h },
  ];
  return localCorners.map((corner) => ({
    x: x + (corner.x * cos - corner.y * sin),
    y: y + (corner.x * sin + corner.y * cos),
  }));
}

export function isPointInModule(point, module, tolerance = 0.01) {
  const local = getLocalCoordinates(point.x, point.y, module);
  return (
    local.x >= -tolerance &&
    local.x <= module.width + tolerance &&
    local.y >= -tolerance &&
    local.y <= module.height + tolerance
  );
}

export function checkOverlapWithRotation(moduleA, moduleB) {
  if (!moduleA || !moduleB || moduleA.id === moduleB.id) return false;
  const verticesA = getModuleVertices(moduleA);
  const verticesB = getModuleVertices(moduleB);
  return checkPolygonsIntersectionSAT(verticesA, verticesB);
}

// Нова функція для перевірки дотику кутами
export function areModulesTouchingAtCorner(moduleA, moduleB) {
  if (!moduleA || !moduleB || moduleA.id === moduleB.id) return false;
  const verticesA = getModuleVertices(moduleA);
  const verticesB = getModuleVertices(moduleB);

  for (const vA of verticesA) {
    for (const vB of verticesB) {
      if (distance(vA, vB) < CORNER_TOUCH_TOLERANCE) {
        return true;
      }
    }
  }
  return false;
}

export function checkForIsolatedModules(modules, joints) {
  if (modules.length <= 1) {
    return false;
  }
  const adj = new Map();
  modules.forEach((m) => adj.set(m.id, []));
  joints.forEach((joint) => {
    if (adj.has(joint.moduleA_id) && adj.has(joint.moduleB_id)) {
      adj.get(joint.moduleA_id).push(joint.moduleB_id);
      adj.get(joint.moduleB_id).push(joint.moduleA_id);
    }
  });
  const visited = new Set();
  let componentCount = 0;
  function dfs(moduleId) {
    visited.add(moduleId);
    (adj.get(moduleId) || []).forEach((neighborId) => {
      if (!visited.has(neighborId)) {
        dfs(neighborId);
      }
    });
  }
  modules.forEach((m) => {
    if (!visited.has(m.id)) {
      dfs(m.id);
      componentCount++;
    }
  });
  return componentCount > 1;
}

export function validateProjectState(projectObjects, floorsArray = [], t) {
  for (const floorId in projectObjects) {
    const floorObjects = projectObjects[floorId];
    const modules = floorObjects.filter((o) => o.type === OBJECT_TYPES.MODULE);
    const joints = floorObjects.filter(
      (o) => o.type === OBJECT_TYPES.DOCKING_JOINT,
    );
    if (checkForIsolatedModules(modules, joints)) {
      const floor = floorsArray.find((f) => f.id === floorId);
      const floorName = floor ? floor.name : `ID: ${floorId}`;
      const message = t
        ? t("toasts.unconnectedModulesError", { floorName })
        : `Error: Unconnected modules found on floor ${floorName}`;
      toast.error(message);
      return false;
    }
  }
  return true;
}

export function getLocalCoordinates(worldX, worldY, module) {
  const translatedWorldX = worldX - module.x;
  const translatedWorldY = worldY - module.y;
  const angleRad = (-(module.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  const localX = translatedWorldX * cos - translatedWorldY * sin;
  const localY = translatedWorldX * sin + translatedWorldY * cos;
  return { x: localX, y: localY };
}

export function worldToScreen(worldPoint, viewTransform) {
  const scale = viewTransform.scale;
  return {
    x: worldPoint.x * scale + viewTransform.x,
    y: worldPoint.y * scale + viewTransform.y,
  };
}

export function getModuleSides(module) {
  const vertices = getModuleVertices(module);
  return {
    top: [vertices[0], vertices[1]],
    right: [vertices[1], vertices[2]],
    bottom: [vertices[2], vertices[3]],
    left: [vertices[3], vertices[0]],
  };
}

export function findDockingPair(moduleA, moduleB, tolerance = 0.01) {
  const sidesA = getModuleSides(moduleA);
  const sidesB = getModuleSides(moduleB);

  for (const sideKeyA in sidesA) {
    for (const sideKeyB in sidesB) {
      const sideA = sidesA[sideKeyA];
      const sideB = sidesB[sideKeyB];
      const [p1_A, p2_A] = sideA;
      const [p1_B, p2_B] = sideB;

      const lineVecA = subtractVectors(p2_A, p1_A);
      const normA = normalizeVector(perpendicularVector(lineVecA));
      const dist1 = Math.abs(dotProduct(subtractVectors(p1_B, p1_A), normA));
      const dist2 = Math.abs(dotProduct(subtractVectors(p2_B, p1_A), normA));

      if (dist1 < tolerance && dist2 < tolerance) {
        const segDirA = normalizeVector(lineVecA);
        const proj = (p) => dotProduct(p, segDirA);

        const proj_p1A = proj(p1_A);
        const proj_p2A = proj(p2_A);
        const proj_p1B = proj(p1_B);
        const proj_p2B = proj(p2_B);

        const minA = Math.min(proj_p1A, proj_p2A);
        const maxA = Math.max(proj_p1A, proj_p2A);
        const minB = Math.min(proj_p1B, proj_p2B);
        const maxB = Math.max(proj_p1B, proj_p2B);

        const overlap_min_proj = Math.max(minA, minB);
        const overlap_max_proj = Math.min(maxA, maxB);
        const overlapLen = overlap_max_proj - overlap_min_proj;

        if (overlapLen > tolerance) {
          const lenA = distance(p1_A, p2_A);
          const lenB = distance(p1_B, p2_B);

          // If modules are docked, their sides must be of the same length and fully overlap.
          // This rule applies to all modules, including corridors.
          const isValidDock =
            Math.abs(lenA - lenB) < tolerance &&
            Math.abs(overlapLen - lenA) < tolerance;

          if (isValidDock) {
            const basePoint =
              dotProduct(p1_A, segDirA) < dotProduct(p2_A, segDirA)
                ? p1_A
                : p2_A;
            const startOffset = overlap_min_proj - Math.min(proj_p1A, proj_p2A);
            const p_start = addVectors(
              basePoint,
              scaleVector(segDirA, startOffset),
            );
            const p_end = addVectors(p_start, scaleVector(segDirA, overlapLen));

            return {
              moduleA_id: moduleA.id,
              moduleB_id: moduleB.id,
              overlapSegment: [p_start, p_end],
            };
          }
        }
      }
    }
  }
  return null;
}

export function areSidesCollinearAndOverlapping(
  moduleA,
  moduleB,
  tolerance = 0.01,
) {
  const sidesA = getModuleSides(moduleA);
  const sidesB = getModuleSides(moduleB);

  for (const sideKeyA in sidesA) {
    for (const sideKeyB in sidesB) {
      const sideA = sidesA[sideKeyA];
      const sideB = sidesB[sideKeyB];
      const [p1_A, p2_A] = sideA;
      const [p1_B, p2_B] = sideB;

      const lineVecA = subtractVectors(p2_A, p1_A);
      if (distance(p1_A, p2_A) < tolerance) continue;
      const normA = normalizeVector(perpendicularVector(lineVecA));

      const dist1 = Math.abs(dotProduct(subtractVectors(p1_B, p1_A), normA));
      const dist2 = Math.abs(dotProduct(subtractVectors(p2_B, p1_A), normA));

      if (dist1 < tolerance && dist2 < tolerance) {
        const segDirA = normalizeVector(lineVecA);
        const proj = (p) => dotProduct(p, segDirA);

        const proj_p1A = proj(p1_A);
        const proj_p2A = proj(p2_A);
        const proj_p1B = proj(p1_B);
        const proj_p2B = proj(p2_B);

        const minA = Math.min(proj_p1A, proj_p2A);
        const maxA = Math.max(proj_p1A, proj_p2A);
        const minB = Math.min(proj_p1B, proj_p2B);
        const maxB = Math.max(proj_p1B, proj_p2B);

        const overlap_min_proj = Math.max(minA, minB);
        const overlap_max_proj = Math.min(maxA, maxB);
        const overlapLen = overlap_max_proj - overlap_min_proj;

        if (overlapLen > tolerance) {
          return true;
        }
      }
    }
  }
  return false;
}

function isTeeJunction(mod1, mod2, tolerance) {
  const vertices1 = getModuleVertices(mod1);
  const vertices2 = getModuleVertices(mod2);

  const check = (vertsA, vertsB) => {
    for (const vA of vertsA) {
      for (let i = 0; i < vertsB.length; i++) {
        const p1 = vertsB[i];
        const p2 = vertsB[(i + 1) % vertsB.length];

        const lineVec = subtractVectors(p2, p1);
        const pointVec = subtractVectors(vA, p1);
        const lineLenSq = dotProduct(lineVec, lineVec);

        if (lineLenSq < 1e-9) continue;

        const t = dotProduct(pointVec, lineVec) / lineLenSq;

        if (t >= 0 && t <= 1) {
          const projection = addVectors(p1, scaleVector(lineVec, t));
          if (distance(vA, projection) < tolerance) {
            if (distance(vA, p1) > tolerance && distance(vA, p2) > tolerance) {
              return true;
            }
          }
        }
      }
    }
    return false;
  };

  return check(vertices1, vertices2) || check(vertices2, vertices1);
}

export function checkInvalidAdjacency(moduleA, moduleB, tolerance = 0.05) {
  // Удален ошибочный блок, который помечал правильное примыкание как недопустимое.

  if (isTeeJunction(moduleA, moduleB, tolerance)) {
    return true;
  }

  return false;
}

export function getWallSegmentKeyFromLocal(
  localX,
  localY,
  module,
  tolerance = 0.1,
) {
  const { cellsWide, cellsLong, width, height } = module;
  if (
    Math.abs(localY) < tolerance &&
    localX >= -tolerance &&
    localX <= width + tolerance
  ) {
    const cellX = Math.min(
      cellsWide - 1,
      Math.max(0, Math.floor(localX / GRID_CELL_SIZE_M)),
    );
    return `${cellX},0_h`;
  }
  if (
    Math.abs(localY - height) < tolerance &&
    localX >= -tolerance &&
    localX <= width + tolerance
  ) {
    const cellX = Math.min(
      cellsWide - 1,
      Math.max(0, Math.floor(localX / GRID_CELL_SIZE_M)),
    );
    return `${cellX},${cellsLong}_h`;
  }
  if (
    Math.abs(localX) < tolerance &&
    localY >= -tolerance &&
    localY <= height + tolerance
  ) {
    const cellY = Math.min(
      cellsLong - 1,
      Math.max(0, Math.floor(localY / GRID_CELL_SIZE_M)),
    );
    return `0,${cellY}_v`;
  }
  if (
    Math.abs(localX - width) < tolerance &&
    localY >= -tolerance &&
    localY <= height + tolerance
  ) {
    const cellY = Math.min(
      cellsLong - 1,
      Math.max(0, Math.floor(localY / GRID_CELL_SIZE_M)),
    );
    return `${cellsWide},${cellY}_v`;
  }
  return null;
}

export function findOppositeSegmentOnJoint(
  joint,
  sourceModuleId,
  targetSlotIndex,
  allModulesOnFloor,
) {
  if (!joint || !sourceModuleId || targetSlotIndex < 0 || !allModulesOnFloor)
    return null;
  const otherModuleId =
    joint.moduleA_id === sourceModuleId ? joint.moduleB_id : joint.moduleA_id;
  const otherModule = allModulesOnFloor.find((m) => m.id === otherModuleId);
  if (!otherModule) return null;
  const numSlots = Math.max(1, Math.round(joint.width / GRID_CELL_SIZE_M));
  const segmentRatio = (targetSlotIndex + 0.5) / numSlots;
  const jointVectorFull = rotatePoint(
    { x: joint.width, y: 0 },
    { x: 0, y: 0 },
    joint.rotation,
  );
  const worldJointStart = {
    x: joint.x - jointVectorFull.x / 2,
    y: joint.y - jointVectorFull.y / 2,
  };
  const pointOnJointCenterline = addVectors(
    worldJointStart,
    scaleVector(jointVectorFull, segmentRatio),
  );
  const perpendicularToJoint = normalizeVector(
    perpendicularVector(jointVectorFull),
  );
  const otherModuleCenterLocal = {
    x: otherModule.width / 2,
    y: otherModule.height / 2,
  };
  const otherModuleCenterRotated = rotatePoint(
    otherModuleCenterLocal,
    { x: 0, y: 0 },
    otherModule.rotation || 0,
  );
  const otherModuleCenterWorld = addVectors(
    { x: otherModule.x, y: otherModule.y },
    otherModuleCenterRotated,
  );
  const vecToModuleCenter = subtractVectors(
    otherModuleCenterWorld,
    pointOnJointCenterline,
  );
  let directionFactor = Math.sign(
    dotProduct(vecToModuleCenter, perpendicularToJoint),
  );
  if (directionFactor === 0) {
    const sourceModule = allModulesOnFloor.find((m) => m.id === sourceModuleId);
    if (sourceModule) {
      const sourceModuleCenterLocal = {
        x: sourceModule.width / 2,
        y: sourceModule.height / 2,
      };
      const sourceModuleCenterRotated = rotatePoint(
        sourceModuleCenterLocal,
        { x: 0, y: 0 },
        sourceModule.rotation || 0,
      );
      const sourceModuleCenterWorld = addVectors(
        { x: sourceModule.x, y: sourceModule.y },
        sourceModuleCenterRotated,
      );
      const vecSourceToTargetModule = subtractVectors(
        otherModuleCenterWorld,
        sourceModuleCenterWorld,
      );
      directionFactor =
        Math.sign(dotProduct(vecSourceToTargetModule, perpendicularToJoint)) ||
        1;
    } else {
      directionFactor = 1;
    }
  }
  const offsetDistance = 0.15;
  const testPointWorld = addVectors(
    pointOnJointCenterline,
    scaleVector(perpendicularToJoint, offsetDistance * directionFactor),
  );
  const localCoordsInOtherModule = getLocalCoordinates(
    testPointWorld.x,
    testPointWorld.y,
    otherModule,
  );
  const oppositeSegmentKey = getWallSegmentKeyFromLocal(
    localCoordsInOtherModule.x,
    localCoordsInOtherModule.y,
    otherModule,
  );
  if (
    oppositeSegmentKey &&
    otherModule.wallSegments &&
    otherModule.wallSegments[oppositeSegmentKey]
  ) {
    const segmentData = otherModule.wallSegments[oppositeSegmentKey];
    const [coords, orientation] = oppositeSegmentKey.split("_");
    const [cxStr, cyStr] = coords.split(",");
    const cellX = parseInt(cxStr);
    const cellY = parseInt(cyStr);
    const isExternal =
      (orientation === "h" &&
        (cellY === 0 || cellY === otherModule.cellsLong)) ||
      (orientation === "v" && (cellX === 0 || cellX === otherModule.cellsWide));
    return {
      ...segmentData,
      moduleId: otherModule.id,
      segmentKeyInParentModule: oppositeSegmentKey,
      cellX: cellX,
      cellY: cellY,
      orientation: orientation,
      parentModuleCellsWide: otherModule.cellsWide,
      parentModuleCellsLong: otherModule.cellsLong,
      isExternal: isExternal,
    };
  }
  return null;
}

/**
 * Finds all ghost modules that geometrically overlap with the module being placed.
 * @param {object} moduleToPlace The module being placed on the current floor.
 * @param {array} ghostModules The array of modules from the floor below.
 * @returns {array} An array of ghost modules that are underneath the module being placed.
 */
export function getUnderlyingModules(moduleToPlace, ghostModules) {
  return ghostModules.filter((ghost) =>
    checkOverlapWithRotation(moduleToPlace, ghost),
  );
}

/**
 * Validates if a module can be placed on top of modules from the floor below.
 * Checks for full support, subtype compatibility, and footprint matching.
 * @param {object} moduleToPlace The module being placed.
 * @param {array} ghostModules Modules from the floor below.
 * @param {function} t The translation function.
 * @returns {{isValid: boolean, message: string}} Validation result.
 */
export function validateModulePlacement(moduleToPlace, ghostModules, t) {
  // 1. Check for full support (all vertices must be on a ghost module)
  const vertices = getModuleVertices(moduleToPlace);
  const isSupported = vertices.every((vertex) =>
    ghostModules.some((ghostModule) => isPointInModule(vertex, ghostModule)),
  );

  if (!isSupported) {
    return { isValid: false, message: t("toasts.modulePlacementError") };
  }

  // 2. Get the specific modules it rests on
  const underlyingModules = getUnderlyingModules(moduleToPlace, ghostModules);

  if (underlyingModules.length === 0) {
    return { isValid: false, message: t("toasts.modulePlacementError") };
  }

  // 3. Check subtype compatibility
  const targetSubtype = moduleToPlace.subtype || "residential";
  const allowedOnTop = PLACEMENT_RULES[targetSubtype];

  if (allowedOnTop) {
    const allUnderlyingValid = underlyingModules.every((um) =>
      allowedOnTop.includes(um.subtype),
    );
    if (!allUnderlyingValid) {
      const underlyingSubtypes = [
        ...new Set(underlyingModules.map((um) => um.subtype)),
      ].join(", ");
      return {
        isValid: false,
        message: t("toasts.placementMismatchError", {
          targetSubtype,
          underlyingSubtypes,
        }),
      };
    }
  }

  // 4. Check footprint consistency
  const topArea = moduleToPlace.width * moduleToPlace.height;
  const bottomArea = underlyingModules.reduce(
    (acc, um) => acc + um.width * um.height,
    0,
  );

  const areaEpsilon = 0.01;
  if (Math.abs(topArea - bottomArea) > areaEpsilon) {
    return {
      isValid: false,
      message: t("toasts.footprintMismatchError", {
        topArea: topArea.toFixed(1),
        bottomArea: bottomArea.toFixed(1),
      }),
    };
  }

  return { isValid: true, message: "" };
}

export function findOverlappingSegment(moduleA, moduleB, tolerance = 0.01) {
  const sidesA = getModuleSides(moduleA);
  const sidesB = getModuleSides(moduleB);

  for (const sideKeyA in sidesA) {
    for (const sideKeyB in sidesB) {
      const sideA = sidesA[sideKeyA];
      const sideB = sidesB[sideKeyB];
      const [p1_A, p2_A] = sideA;
      const [p1_B, p2_B] = sideB;

      // Проверяем, лежат ли стороны на одной прямой (коллинеарны)
      const lineVecA = subtractVectors(p2_A, p1_A);
      const normA = normalizeVector(perpendicularVector(lineVecA));
      const dist1 = Math.abs(dotProduct(subtractVectors(p1_B, p1_A), normA));
      const dist2 = Math.abs(dotProduct(subtractVectors(p2_B, p1_A), normA));

      if (dist1 < tolerance && dist2 < tolerance) {
        // Проецируем точки на общую ось, чтобы найти длину перекрытия
        const segDirA = normalizeVector(lineVecA);
        const proj = (p) => dotProduct(p, segDirA);

        const minA = Math.min(proj(p1_A), proj(p2_A));
        const maxA = Math.max(proj(p1_A), proj(p2_A));
        const minB = Math.min(proj(p1_B), proj(p2_B));
        const maxB = Math.max(proj(p1_B), proj(p2_B));

        const overlap_min_proj = Math.max(minA, minB);
        const overlap_max_proj = Math.min(maxA, maxB);
        const overlapLen = overlap_max_proj - overlap_min_proj;

        // Если есть хоть какое-то перекрытие, мы нашли стык
        if (overlapLen > tolerance) {
          const basePoint =
            dotProduct(p1_A, segDirA) < dotProduct(p2_A, segDirA) ? p1_A : p2_A;
          const startOffset =
            overlap_min_proj - Math.min(proj(p1_A), proj(p2_A));
          const p_start = addVectors(
            basePoint,
            scaleVector(segDirA, startOffset),
          );
          const p_end = addVectors(p_start, scaleVector(segDirA, overlapLen));

          return {
            moduleA_id: moduleA.id,
            moduleB_id: moduleB.id,
            overlapSegment: [p_start, p_end],
          };
        }
      }
    }
  }
  return null;
}